/* SIZESOFTX
=========================================================================================
* Descripción: Modal de generación de vales por cambio
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 17/08/2026 | Lic. José Luis Aragón Cervantes  | Creación de generación de vales por cambio
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { useEffect, useRef, useState } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import SizeSoftXModal from "../leyout/SizeSoftXModal";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { useSesion } from "../../context/System/SesionContext";
import { apiConsultarFolioVale, apiGenerarValePorCambio } from "../../settings/ApiConfig";
import { handleValidarStrings } from "../../settings/utils";

const formatCurrency = (value) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(value || 0));

const sumarDias = (dias) => {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() + dias);
    return fecha.toISOString().slice(0, 10);
};

const hoyLocal = () => {
    const fecha = new Date();
    const offset = fecha.getTimezoneOffset();
    return new Date(fecha.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const initialForm = {
    remisionorigen: "",
    monto: "",
    observaciones: "",
};

export default function ModalGenerarValePorCambio({ open, onClose, onSaved, caja = "", usuarioAutoriza = "", usuario = "" }) {
    const { GetRequest, PostRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const { usuarioData, parametrosCajaForm } = useSesion();
    const [folio, setFolio] = useState(0);
    const [form, setForm] = useState(initialForm);
    const [errores, setErrores] = useState({});
    const [guardando, setGuardando] = useState(false);
    const refRemisionOrigen = useRef(null);
    const refMonto = useRef(null);
    const refObservaciones = useRef(null);

    useEffect(() => {
        if (!open) return;
        setForm({ ...initialForm });
        setErrores({});
        const cargarFolio = async () => {
            const response = await GetRequest({ url: apiConsultarFolioVale, loader: "Consultando folio disponible..." });
            if (!response.hasError) setFolio(Number(response.result || 0));
        };
        cargarFolio();
        setTimeout(() => {
            refRemisionOrigen.current?.focus?.();
            refRemisionOrigen.current?.select?.();
        }, 200);
    }, [open]);

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrores((prev) => ({ ...prev, [field]: "" }));
    };

    const handleRemisionOrigenChange = (value) => {
        setField("remisionorigen", String(value ?? "").replace(/\D/g, "").slice(0, 10));
    };

    const handleMontoChange = (value) => {
        let limpio = String(value ?? "").replace(/[^0-9.]/g, "");
        const partes = limpio.split(".");
        if (partes.length > 2) limpio = partes[0] + "." + partes.slice(1).join("");
        const [enteros, decimales] = limpio.split(".");
        limpio = enteros.slice(0, 10) + (decimales !== undefined ? "." + decimales.slice(0, 2) : (limpio.includes(".") ? "." : ""));
        setField("monto", limpio);
    };

    const validar = () => {
        const nuevosErrores = {};
        const remisionorigen = Number(form.remisionorigen || 0);
        if (!(remisionorigen > 0)) nuevosErrores.remisionorigen = "Campo Remisión origen requerido";

        const monto = Number(form.monto || 0);
        if (!(monto > 0)) nuevosErrores.monto = "El monto debe ser mayor a cero";

        if (form.observaciones?.trim()) {
            const errObservaciones = handleValidarStrings(form.observaciones?.trim(), "Observaciones");
            if (errObservaciones) nuevosErrores.observaciones = errObservaciones;
        }

        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const imprimirVale = async (vale) => {
        const printerName = parametrosCajaForm?.nombreimpresoraventas?.trim()
            || localStorage.getItem("nombreimpresoraventas")
            || "";
        if (!printerName) return;

        localStorage.setItem("nombreimpresoraventas", printerName);

        const textEncoder = new TextEncoder();
        const ticketParts = [
            textEncoder.encode('\x1B\x40'),
            textEncoder.encode('\x1B\x4B\x60'),
        ];

        ticketParts.push(
            textEncoder.encode('\x1B\x33\x00'),
            textEncoder.encode('\x1B\x61\x01\x1B\x21\x11' + 'VALE POR CAMBIO\n' + '\x1B\x21\x00'),
            textEncoder.encode('\x1B\x32'),
            textEncoder.encode('\x1B\x61\x00\x1B\x21\x00' + '-'.repeat(32) + '\n'),
            textEncoder.encode('\x1B\x45\x01Folio: ' + (vale.foliovale || "") + '\x1B\x45\x00\n'),
            textEncoder.encode('Fecha: ' + hoyLocal() + '\n'),
            textEncoder.encode('Caja: ' + (caja || parametrosCajaForm?.caja || "") + '\n'),
            textEncoder.encode('Usuario: ' + (usuario || usuarioData?.Usuario || "") + '\n'),
            textEncoder.encode('Autoriza: ' + (usuarioAutoriza || "") + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n'),
            textEncoder.encode('\x1B\x45\x01Monto: ' + formatCurrency(vale.monto || 0) + '\x1B\x45\x00\n'),
            textEncoder.encode('Caducidad: ' + (vale.fechacaducidad || sumarDias(30)) + '\n'),
            textEncoder.encode('-'.repeat(32) + '\n')
        );

        // Código de barras Code128 (auto, set B) con el folio numérico para escéner en cobro
        const folioBarcode = String(vale.foliovale ?? "").replace(/\D/g, "");
        if (folioBarcode.length > 0) {
            const barcodeData = textEncoder.encode("{B" + folioBarcode);
            const gsK = new Uint8Array(4 + barcodeData.length);
            gsK.set([0x1D, 0x6B, 0x49, barcodeData.length], 0);
            gsK.set(barcodeData, 4);
            ticketParts.push(
                textEncoder.encode('\x1B\x61\x01'),
                textEncoder.encode('\x1D\x68\x50'),
                textEncoder.encode('\x1D\x77\x02'),
                gsK,
                textEncoder.encode('\n' + folioBarcode + '\n'),
                textEncoder.encode('\x1B\x61\x00' + '-'.repeat(32) + '\n')
            );
        }

        ticketParts.push(
            textEncoder.encode('\x1B\x61\x01' + 'Presentar este vale en caja\n'),
            textEncoder.encode('para aplicar su cambio.\n\n\n'),
            textEncoder.encode('\x1D\x56\x00'),
        );

        const totalLength = ticketParts.reduce((sum, part) => sum + part.length, 0);
        const finalTicket = new Uint8Array(totalLength);
        let offset = 0;
        ticketParts.forEach(part => {
            finalTicket.set(part, offset);
            offset += part.length;
        });

        const formData = new FormData();
        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", printerName);

        const logoBase64 = parametrosCajaForm?.logo;
        if (logoBase64) {
            try {
                const binary = atob(logoBase64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                formData.append("logo", new Blob([bytes], { type: "image/png" }), "logocliente.png");
            } catch (logoErr) {
                // Logo no disponible, continuar sin él
            }
        }

        await fetch("http://localhost:4000/api/imprimirticket", { method: "POST", body: formData }).catch(() => {});
    };

    const guardar = async () => {
        if (!validar()) return;

        setGuardando(true);
        try {
            const body = {
                remisionorigen: Number(form.remisionorigen || 0),
                monto: Number(form.monto || 0),
                descripcion: "VALE POR CAMBIO",
                fechacaducidad: sumarDias(30),
                observaciones: form.observaciones?.trim() || "",
                usuario: usuario || usuarioData?.Usuario || "",
                usuarioautoriza: usuarioAutoriza || usuarioData?.Usuario || "",
                pc: caja || parametrosCajaForm?.caja || "",
            };

            const response = await PostRequest({ url: apiGenerarValePorCambio, body, loader: "Generando vale por cambio..." });
            if (response.hasError) {
                handleOpenAlert(response.message || "No fue posible generar el vale por cambio", "error");
                return;
            }

            await imprimirVale(response.result || {}).catch(() => {});
            handleOpenAlert(`Vale por cambio ${response.result?.foliovale || ""} generado correctamente`, "success");
            onSaved?.(response.result);
            onClose?.();
        } catch (error) {
            handleOpenAlert("Error al generar el vale por cambio: " + error.message, "error");
        } finally {
            setGuardando(false);
        }
    };

    return (
        <SizeSoftXModal open={open} title="Generar vale por cambio" maxWidth="md" success="Generar" onSuccess={guardar} onClose={onClose} disableSuccess={guardando}>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                        <TextField autoComplete="off" fullWidth size="small" label="Folio vale" value={folio || ""} disabled />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField autoComplete="off"
                            fullWidth
                            size="small"
                            label="Remisión origen"
                            type="text"
                            inputRef={refRemisionOrigen}
                            value={form.remisionorigen}
                            error={!!errores.remisionorigen}
                            helperText={errores.remisionorigen || ""}
                            onChange={(e) => handleRemisionOrigenChange(e.target.value)}
                            onFocus={(e) => e.target.select?.()}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    refMonto.current?.focus?.();
                                    refMonto.current?.select?.();
                                }
                            }}
                            inputProps={{ inputMode: "numeric" }}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField autoComplete="off"
                            fullWidth
                            size="small"
                            label="Monto"
                            type="text"
                            inputRef={refMonto}
                            value={form.monto}
                            error={!!errores.monto}
                            helperText={errores.monto || ""}
                            onChange={(e) => handleMontoChange(e.target.value)}
                            onFocus={(e) => e.target.select?.()}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    refObservaciones.current?.focus?.();
                                    refObservaciones.current?.select?.();
                                }
                            }}
                            inputProps={{ inputMode: "decimal" }}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField autoComplete="off"
                            fullWidth
                            size="small"
                            label="Fecha caducidad"
                            type="date"
                            value={sumarDias(30)}
                            disabled
                        />
                    </Grid>
                </Grid>
                <TextField autoComplete="off"
                    fullWidth
                    size="small"
                    label="Descripción vale"
                    value="VALE POR CAMBIO"
                    disabled
                />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField autoComplete="off" fullWidth size="small" label="Status" value="ACTIVO" disabled />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField autoComplete="off"
                            fullWidth
                            size="small"
                            label="Observaciones"
                            inputRef={refObservaciones}
                            value={form.observaciones}
                            error={!!errores.observaciones}
                            helperText={errores.observaciones || ""}
                            onChange={(e) => setField("observaciones", e.target.value)}
                            onFocus={(e) => e.target.select?.()}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ textAlign: "right" }}>
                    <Typography fontWeight={700}>Monto del vale: {formatCurrency(form.monto)}</Typography>
                </Box>
            </Stack>
        </SizeSoftXModal>
    );
}
